/**
 * Child Work Orders (Feb 2026 · Phase 2)
 * ---------------------------------------
 * Standalone page showing every Child Work Order emitted from any
 * parent Work Order — so shop-floor operators can find their queue
 * directly instead of drilling into the parent WO's Explode modal.
 *
 * Filters: Status (Pending / Ready / In Progress / Completed) and
 * Stage. Each row surfaces the parent WO + source SO/ESO chip so
 * traceability back to the buyer is one glance away. Inline Start /
 * Complete actions call the same PATCH endpoint used by the parent
 * modal — status cascades on the backend keep downstream rows in sync.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import TwoLineDocNo from "@/components/TwoLineDocNo";
import { TreeStructureIcon, ArrowsClockwiseIcon, EyeIcon, PrinterIcon, StackIcon, TrashIcon } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { fulfillmentType, FULFILLMENT_BADGE, FULFILLMENT_INTERNAL } from "@/lib/fulfillment";
import { fmtDateIST, fmtTime12IST, fmtDateTime12IST } from "@/lib/datetime";
import { RowActions } from "@/components/RowActions";
import { HoldReleaseButtons, OnHoldBadge } from "@/components/WoHoldRelease";

// Feb 27, 2026 · Stage → department mapping so Child-WO rows can be
// tagged Warehouse Fulfillment vs Internal Handoff even though the
// CWO itself carries only a `stage_code`.
const STAGE_TO_DEPT = {
  WARPING: "Warping",
  WEAVING: "Weaving",
  COVERING: "Covering",
  SPANDEX_WARPING: "Spandex Warping",
};

const STATUS_TONE = {
  Ready:         "bg-sky-500/15 text-sky-300 border-sky-500/30",
  Pending:       "bg-zinc-500/15 text-zinc-300 border-zinc-500/30",
  "In Progress": "bg-amber-500/15 text-amber-300 border-amber-500/30",
  Completed:     "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  Cancelled:     "bg-red-500/15 text-red-300 border-red-500/30",
};

const fmtQty = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });

// P17-UAT-14 · authoritative manufactured OUTPUT product identity per
// preparatory stage. PURE field read from the CWO read model — the codes are
// generated/resolved by BOM Explosion / the Phase-3J.9A SFW auto-generate hook
// (see REV-A discovery). NEVER construct a code in the frontend.
export const OUTPUT_BADGE = { WARPING: "SFW", COVERING: "SFC", SPANDEX_WARPING: "SFSW" };
// REV-C · per-Warping-item authoritative output (1 RM line → 1 SFW). Read ONLY
// this item's stored field — never items[0] reuse, never constructed.
export const resolveWarpingItemOutput = (it) => (it && (it.output_sfw_code || it.sf_code)) || "";
export const resolveOutputCode = (r) => {
  const it = (r.items || [])[0] || {};
  if (r.stage_code === "WARPING") return it.output_sfw_code || it.sf_code || "";
  if (r.stage_code === "COVERING") return r.output_sfc_code || it.output_sfc_code || "";
  if (r.stage_code === "SPANDEX_WARPING") return it.output_sfsw_code || "";
  return "";
};

export default function ChildWorkOrders() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("all");
  const [stageFilter, setStageFilter] = useState("all");
  const [q, setQ] = useState("");
  const [viewing, setViewing] = useState(null);
  const [editItems, setEditItems] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/child-work-orders");
      setRows(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Child WOs");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const patch = async (cwo, next) => {
    try {
      await api.patch(`/child-work-orders/${cwo.id}`, { status: next });
      toast.success(`${cwo.cwo_no} · ${next}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  // Print/PDF — Blob download so the axios auth header is preserved,
  // then open the object URL in a new tab. Revoked after 1 min.
  const printPdf = async (cwo) => {
    try {
      const res = await api.get(`/child-work-orders/${cwo.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF failed");
    }
  };

  // Open View dialog — snapshot editable qty_produced per RM line so
  // planners can key in shop-floor production without hitting Save on
  // every keystroke.
  const openView = (cwo) => {
    setViewing(cwo);
    setEditItems((cwo.items || []).map(it => ({ ...it, qty_produced: it.qty_produced ?? 0 })));
  };

  const closeView = () => { setViewing(null); setEditItems([]); };

  const saveLines = async () => {
    if (!viewing) return;
    setSaving(true);
    try {
      const payload = { items: editItems.map(it => ({ rm_code: it.rm_code, qty_produced: Number(it.qty_produced) || 0 })) };
      const { data } = await api.put(`/child-work-orders/${viewing.id}/lines`, payload);
      toast.success(`${viewing.cwo_no} · saved · status ${data.status}`);
      setViewing(data);
      setEditItems((data.items || []).map(it => ({ ...it, qty_produced: it.qty_produced ?? 0 })));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const stageOptions = useMemo(
    () => Array.from(new Set(rows.map(r => r.stage_code).filter(Boolean))).sort(),
    [rows]
  );

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter(r => {
      if (status !== "all" && r.status !== status) return false;
      if (stageFilter !== "all" && r.stage_code !== stageFilter) return false;
      if (needle) {
        const hay = [r.cwo_no, r.parent_wo_no, r.source_order_no, r.customer_name, r.stage_code, r.stage_name].join(" ").toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [rows, status, stageFilter, q]);

  const kpis = useMemo(() => ({
    total: rows.length,
    ready: rows.filter(r => r.status === "Ready").length,
    inProgress: rows.filter(r => r.status === "In Progress").length,
    completed: rows.filter(r => r.status === "Completed").length,
    pending: rows.filter(r => r.status === "Pending").length,
  }), [rows]);

  return (
    <div className="p-6 space-y-5" data-testid="child-wos-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-1 flex items-center gap-2"><TreeStructureIcon size={11} weight="bold" />Production · Child WOs</div>
          <h1 className="font-display text-3xl font-black tracking-tight text-white">Child Work Orders</h1>
          <div className="text-xs text-zinc-500 mt-1">Every stage-level work order emitted from a parent WO · start / complete stages from here without opening the parent</div>
        </div>
        <button onClick={load} disabled={loading} className="h-10 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-white text-sm rounded-sm flex items-center gap-2 disabled:opacity-60">
          <ArrowsClockwiseIcon size={14} weight="bold" />Refresh
        </button>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label="Total" value={kpis.total} tone="zinc" />
        <Kpi label="Pending" value={kpis.pending} tone="zinc" />
        <Kpi label="Ready" value={kpis.ready} tone="sky" />
        <Kpi label="In Progress" value={kpis.inProgress} tone="amber" />
        <Kpi label="Completed" value={kpis.completed} tone="emerald" />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap text-[11px]">
        <div className="flex items-center gap-2">
          <span className="text-zinc-500 font-mono uppercase tracking-widest">Status:</span>
          {["all", "Pending", "Ready", "In Progress", "Completed", "Cancelled"].map(s => (
            <button key={s} data-testid={`filter-${s}`} onClick={() => setStatus(s)}
              className={`px-2.5 py-1 rounded-sm border font-mono uppercase tracking-widest ${
                status === s ? "border-[var(--accent)] bg-[var(--accent)]/10 text-white"
                : "border-[var(--border)] text-zinc-500 hover:text-white"
              }`}>{s === "all" ? `All (${rows.length})` : s}</button>
          ))}
        </div>
        {stageOptions.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-zinc-500 font-mono uppercase tracking-widest">Stage:</span>
            <button onClick={() => setStageFilter("all")}
              className={`px-2.5 py-1 rounded-sm border font-mono uppercase tracking-widest ${
                stageFilter === "all" ? "border-[var(--accent)] bg-[var(--accent)]/10 text-white"
                : "border-[var(--border)] text-zinc-500 hover:text-white"
              }`}>All</button>
            {stageOptions.map(s => (
              <button key={s} onClick={() => setStageFilter(s)}
                className={`px-2.5 py-1 rounded-sm border font-mono uppercase tracking-widest ${
                  stageFilter === s ? "border-[var(--accent)] bg-[var(--accent)]/10 text-white"
                  : "border-[var(--border)] text-zinc-500 hover:text-white"
                }`}>{s}</button>
            ))}
          </div>
        )}
        <div className="flex-1 min-w-[220px]">
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search CWO / parent WO / SO / customer…"
            className="w-full h-8 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]" />
        </div>
      </div>

      {/* List */}
      <div className="tactical-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="child-wos-table">
          <thead>
            <tr className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
              <th className="text-left px-3 py-3 font-medium">Child WO No</th>
              <th className="text-left px-3 py-3 font-medium">Date</th>
              <th className="text-left px-3 py-3 font-medium">Parent WO</th>
              <th className="text-left px-3 py-3 font-medium">Source SO / ESO</th>
{/* Customer hidden on production floor · Mar 2026 */}
              <th className="text-left px-3 py-3 font-medium">Stage</th>
              <th className="text-left px-3 py-3 font-medium">Fulfillment</th>
              <th className="text-left px-3 py-3 font-medium w-24">Parallel</th>
              <th className="text-left px-3 py-3 font-medium">Depends On</th>
              <th className="text-left px-3 py-3 font-medium">RM Breakdown · Planned / Produced / Balance</th>
              <th className="text-right px-3 py-3 font-medium">Total Qty</th>
              <th className="text-left px-3 py-3 font-medium">Status</th>
              <th className="text-right px-3 py-3 font-medium">Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => (
              <tr key={r.id} className="border-b border-[var(--border)] hover:bg-[var(--bg-2)]/40 align-top" data-testid={`cwo-row-${r.id}`}>
                <td className="px-3 py-3 text-right"><TwoLineDocNo value={r.cwo_no} className="text-white" /></td>
                <td className="px-3 py-3 font-mono text-zinc-300 whitespace-nowrap" data-testid={`cwo-date-${r.id}`}>{fmtDateIST(r.created_at)}</td>
                <td className="px-3 py-3 font-mono text-[var(--accent)]">{r.parent_wo_no}</td>
                <td className="px-3 py-3 font-mono text-zinc-400">
                  {r.source_order_no ? (
                    <span className="inline-flex items-center gap-1">
                      <span className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border ${r.source_type === "ESO" ? "border-purple-500/30 text-purple-300 bg-purple-500/10" : "border-sky-500/30 text-sky-300 bg-sky-500/10"}`}>{r.source_type || "SO"}</span>
                      {r.source_order_no}
                    </span>
                  ) : <span className="text-zinc-600">—</span>}
                </td>

                <td className="px-3 py-3">
                  <span className="text-white">{r.stage_name}</span>
                  <span className="ml-2 text-[10px] font-mono text-zinc-600">{r.stage_code}</span>
                </td>
                <td className="px-3 py-3">
                  {(() => {
                    const dept = STAGE_TO_DEPT[r.stage_code] || "";
                    const ft = fulfillmentType(dept);
                    if (!ft) return <span className="text-zinc-600 text-[10px]">—</span>;
                    const pendingRM = ft === FULFILLMENT_INTERNAL && (r.status === "Pending" || r.status === "Ready");
                    return (
                      <div className="space-y-0.5">
                        <span data-testid={`cwo-fulfillment-${r.id}`}
                              className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm border ${FULFILLMENT_BADGE[ft] || ""}`}>{ft}</span>
                        {pendingRM && (
                          <div className="text-[9px] font-mono text-purple-300/80">Awaiting preparatory beam</div>
                        )}
                      </div>
                    );
                  })()}
                </td>
                <td className="px-3 py-3">
                  {r.parallel_group ? (
                    <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-sky-500/30 bg-sky-500/10 text-sky-300">{r.parallel_group}</span>
                  ) : <span className="text-zinc-600">—</span>}
                </td>
                <td className="px-3 py-3 font-mono text-[10px] text-zinc-400">{r.depends_on || <span className="text-zinc-600">—</span>}</td>
                <td className="px-3 py-3">
                  {(() => {
                    // Feb 2026 · Always render the mini-grid so per-line
                    // Planned / Produced / Balance columns are visible
                    // even when items[] wasn't captured on the CWO doc
                    // (older CWOs pre-Explode-Refresh only carry
                    // rm_codes[]). Prorates aggregate qty across the rm
                    // list as a defensive fallback.
                    let rows = r.items || [];
                    if ((!rows || rows.length === 0) && (r.rm_codes || []).length > 0) {
                      const per = (Number(r.qty_planned) || 0) / r.rm_codes.length;
                      rows = r.rm_codes.map((c) => ({ rm_code: c, qty: per, uom: r.uom, qty_produced: 0 }));
                    }
                    if (!rows || rows.length === 0) return <span className="text-zinc-600">—</span>;
                    const isPrep = ["WARPING", "COVERING", "SPANDEX_WARPING"].includes(r.stage_code);
                    const isWarping = r.stage_code === "WARPING";
                    return (
                      <div className="space-y-1" data-testid={`cwo-io-${r.id}`}>
                        <div className="border border-[var(--border)]/60 rounded-sm overflow-hidden" data-testid={`cwo-rm-list-${r.id}`}>
                          <div className="grid grid-cols-[minmax(200px,1fr)_72px_72px_72px_32px] gap-2 px-2 py-1 bg-[var(--bg-2)]/60 text-[9px] font-mono uppercase tracking-widest text-zinc-500">
                            <div>{isPrep ? "Input Material" : "RM Code"}</div>
                            <div className="text-right">Planned</div>
                            <div className="text-right">Produced</div>
                            <div className="text-right">Balance</div>
                            <div>UoM</div>
                          </div>
                          {rows.map((it, i) => {
                            const planned = Number(it.qty) || 0;
                            const produced = Number(it.qty_produced) || 0;
                            const balance = Math.max(0, planned - produced);
                            const done = balance === 0 && planned > 0;
                            // REV-C · per-Warping-item authoritative output (1 RM line
                            // → 1 SFW). Read ONLY this item's stored field — never
                            // items[0] reuse, never constructed.
                            const itemSfw = it.output_sfw_code || it.sf_code || "";
                            return (
                              <div key={i} className="border-t border-[var(--border)]/40" data-testid={`cwo-rm-block-${r.id}-${i}`}>
                                <div className="grid grid-cols-[minmax(200px,1fr)_72px_72px_72px_32px] gap-2 px-2 py-1 text-[11px] items-start" data-testid={`cwo-rm-row-${r.id}-${i}`}>
                                  <div className="font-mono text-[var(--accent)] break-all leading-tight" title={it.rm_code} data-testid={`cwo-rm-code-${r.id}-${i}`}>{it.rm_code}</div>
                                  <div className="text-right font-mono tabular-nums text-white">{fmtQty(planned)}</div>
                                  <div className="text-right font-mono tabular-nums text-emerald-300">{fmtQty(produced)}</div>
                                  <div className={`text-right font-mono tabular-nums ${done ? "text-emerald-400" : "text-amber-300"}`}>{fmtQty(balance)}</div>
                                  <div className="text-zinc-500 font-mono">{it.uom}</div>
                                </div>
                                {isWarping && (
                                  <div className="flex items-start gap-2 px-2 pb-1 pl-4" data-testid={`cwo-output-${r.id}-${i}`}>
                                    <span className="text-[9px] font-mono uppercase tracking-widest text-emerald-500/70 shrink-0 pt-0.5">↳ Output</span>
                                    <span className="text-[9px] font-mono font-bold px-1.5 py-0.5 rounded-sm bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 shrink-0" data-testid={`cwo-output-badge-${r.id}-${i}`}>SFW</span>
                                    {itemSfw
                                      ? <span className="font-mono text-[11px] text-emerald-200 break-all leading-tight" title={itemSfw} data-testid={`cwo-output-code-${r.id}-${i}`}>{itemSfw}</span>
                                      : <span className="font-mono text-[11px] text-zinc-600" title="No authoritative output identity on this legacy Warping item" data-testid={`cwo-output-code-${r.id}-${i}`}>—</span>}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        {isPrep && !isWarping && (() => {
                          // Covering (N RM → 1 SFC) and Spandex Warping (1 SFC → 1 SFSW)
                          // carry a single authoritative output for the whole CWO.
                          const outCode = resolveOutputCode(r);
                          const badge = OUTPUT_BADGE[r.stage_code] || "SF";
                          return (
                            <div className="flex items-start gap-2 px-2 py-1 rounded-sm bg-emerald-500/5 border border-emerald-500/20" data-testid={`cwo-output-${r.id}`}>
                              <span className="text-[9px] font-mono uppercase tracking-widest text-emerald-500/70 shrink-0 pt-0.5">↓ Output</span>
                              <span className="text-[9px] font-mono font-bold px-1.5 py-0.5 rounded-sm bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 shrink-0" data-testid={`cwo-output-badge-${r.id}`}>{badge}</span>
                              {outCode
                                ? <span className="font-mono text-[11px] text-emerald-200 break-all leading-tight" title={outCode} data-testid={`cwo-output-code-${r.id}`}>{outCode}</span>
                                : <span className="font-mono text-[11px] text-zinc-600" title="No authoritative output identity on this legacy CWO" data-testid={`cwo-output-code-${r.id}`}>—</span>}
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })()}
                </td>
                <td className="px-3 py-3 text-right font-mono tabular-nums align-top pt-4">
                  <div className="text-white">{fmtQty(r.qty_planned)}</div>
                  <div className="text-[10px] text-emerald-400">produced {fmtQty(r.qty_produced || 0)}</div>
                  <div className="text-[10px] text-amber-300">balance {fmtQty(r.qty_balance ?? (Number(r.qty_planned || 0) - Number(r.qty_produced || 0)))}</div>
                  <div className="text-[10px] text-zinc-500 mt-0.5">{r.uom}</div>
                </td>
                <td className="px-3 py-3">
                  <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm border ${STATUS_TONE[r.status] || ""}`}>{r.status}</span>
                  <div className="mt-1"><OnHoldBadge doc={r} testId={`cwo-on-hold-${r.id}`} /></div>
                </td>
                <td className="px-3 py-3 text-right">
                  <RowActions
                    testid={`cwo-actions-${r.id}`}
                    items={[
                      { label: "View", testid: `view-${r.id}`, onClick: () => openView(r) },
                      { label: "Print PDF", testid: `print-${r.id}`, onClick: () => printPdf(r) },
                    ]}
                  />
                </td>
              </tr>
            ))}
            {filtered.length === 0 && !loading && (
              <tr><td colSpan={11} className="text-center py-16 text-zinc-500">
                {rows.length === 0
                  ? <>No Child WOs yet — open any Work Order and click <span className="text-white font-mono">EXPLODE BOM</span> to generate them.</>
                  : "No Child WOs match the current filters."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Feb 2026 · CWO View dialog — read-only summary + full RM checklist */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && closeView()}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] text-white max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="cwo-view-dialog" description="Child Work Order action — SF stage under a finished-good parent WO.">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-3">
              Child Work Order · {viewing?.cwo_no}
              {viewing && <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm border ${STATUS_TONE[viewing.status] || ""}`}>{viewing.status}</span>}
              <OnHoldBadge doc={viewing} testId="cwo-view-on-hold" />
            </DialogTitle>
          </DialogHeader>
          {viewing && (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-3 text-xs">
                <Meta label="Parent WO" value={viewing.parent_wo_no} mono accent />
                <Meta label="Stage" value={`${viewing.stage_name} · ${viewing.stage_code}`} />
                <Meta label={`${viewing.source_type || "SO"} Reference`} value={viewing.source_order_no} mono />
{/* Customer hidden on production floor · Mar 2026 */}
                <Meta label="Kind" value={viewing.kind} />
                <Meta label="Parallel Group" value={viewing.parallel_group || "—"} />
                <Meta label="Depends On" value={viewing.depends_on || "—"} mono />
                <Meta label="Qty Planned" value={`${fmtQty(viewing.qty_planned)} ${viewing.uom || ""}`} mono />
              </div>
              <div className="border border-[var(--border)] rounded-sm p-3" data-testid="cwo-doc-info">
                <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-2">Document Information</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                  <Meta label="Child WO No." value={viewing.cwo_no} mono accent />
                  <Meta label="Created Date" value={fmtDateIST(viewing.created_at)} mono testid="cwo-created-date" />
                  <Meta label="Created Time" value={fmtTime12IST(viewing.created_at)} mono testid="cwo-created-time" />
                  <Meta label="Created By" value={viewing.created_by || "—"} mono testid="cwo-created-by" />
                  <Meta label="Last Updated" value={fmtDateTime12IST(viewing.updated_at)} mono testid="cwo-last-updated" />
                </div>
              </div>
              <div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-2">Raw Material Checklist · {(viewing.items || []).length}</div>
                <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-[var(--bg-2)]/40 text-[10px] uppercase tracking-widest text-zinc-500">
                      <tr>
                        <th className="text-left px-2 py-2 w-8">#</th>
                        <th className="text-left px-2 py-2">RM Code</th>
                        <th className="text-right px-2 py-2 w-28">Qty Planned</th>
                        <th className="text-right px-2 py-2 w-32">Qty Produced</th>
                        <th className="text-right px-2 py-2 w-28">Qty Balance</th>
                        <th className="text-left px-2 py-2 w-16">UoM</th>
                      </tr>
                    </thead>
                    <tbody>
                      {editItems.map((it, i) => {
                        const planned = Number(it.qty) || 0;
                        const produced = Number(it.qty_produced) || 0;
                        const balance = Math.max(0, planned - produced);
                        return (
                          <tr key={i} className="border-t border-[var(--border)]">
                            <td className="px-2 py-2 font-mono text-zinc-500">{i + 1}</td>
                            <td className="px-2 py-2 font-mono text-[var(--accent)]">{it.rm_code}</td>
                            <td className="px-2 py-2 text-right font-mono tabular-nums text-white">{fmtQty(planned)}</td>
                            <td className="px-2 py-2 text-right">
                              <input type="number" step="0.001" min="0" max={planned}
                                data-testid={`cwo-produced-${i}`}
                                value={it.qty_produced}
                                onChange={e => setEditItems(prev => prev.map((x, j) => j === i ? { ...x, qty_produced: e.target.value } : x))}
                                className="w-24 h-7 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-right text-xs font-mono focus:outline-none focus:border-[var(--accent)]" />
                            </td>
                            <td className={`px-2 py-2 text-right font-mono tabular-nums ${balance === 0 ? "text-emerald-300" : "text-amber-300"}`} data-testid={`cwo-balance-${i}`}>{fmtQty(balance)}</td>
                            <td className="px-2 py-2 text-zinc-400">{it.uom}</td>
                          </tr>
                        );
                      })}
                      {(!editItems || editItems.length === 0) && (
                        <tr><td colSpan={6} className="text-center py-6 text-zinc-500">No RM breakdown captured — re-run Explode BOM on the parent WO</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="text-[10px] text-zinc-600 mt-2">
                  Saving flips status automatically: 0 produced = Ready, partial = In Progress, full = Completed (cascades downstream Ready flips).
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={closeView} className="px-4 py-2 rounded-sm border border-[var(--border)] text-sm text-zinc-400">Close</button>
            {viewing && <HoldReleaseButtons kind="cwo" doc={viewing} onDone={() => { closeView(); load(); }} />}
            {viewing && editItems.length > 0 && (
              <button data-testid="cwo-save-lines" onClick={saveLines} disabled={saving}
                className="px-4 py-2 rounded-sm border border-emerald-500/40 hover:bg-emerald-500/10 text-emerald-300 text-sm font-semibold disabled:opacity-50">
                {saving ? "Saving…" : "Save Production"}
              </button>
            )}
            {viewing && (
              <button data-testid="cwo-view-pdf" onClick={() => printPdf(viewing)}
                className="px-4 py-2 rounded-sm bg-[var(--accent)] hover:bg-blue-600 text-white text-sm font-semibold flex items-center gap-2">
                <PrinterIcon size={14} weight="bold" />Print PDF
              </button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Meta({ label, value, mono, accent, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{label}</div>
      <div data-testid={testid} className={`${mono ? "font-mono" : ""} ${accent ? "text-[var(--accent)]" : "text-white"}`}>{value || "—"}</div>
    </div>
  );
}

function Kpi({ label, value, tone }) {
  const toneMap = {
    zinc:    "border-zinc-500/30 bg-zinc-500/5",
    sky:     "border-sky-500/30 bg-sky-500/5",
    amber:   "border-amber-500/30 bg-amber-500/5",
    emerald: "border-emerald-500/30 bg-emerald-500/5",
  };
  return (
    <div className={`border rounded-sm p-3 ${toneMap[tone] || toneMap.zinc}`} data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1"><StackIcon size={10} />{label}</div>
      <div className="text-2xl font-black text-white font-display mt-1">{value}</div>
    </div>
  );
}
